// Sharing a class with a co-teacher or counselor in the school, until a date: a "view"
// grant reads the roster, sessions, attempts and reports; a "grade" grant can also grade.
// Only the class's teachers and school admins see this section. Expired grants stop
// working on their own; the server checks the expiry on every request.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createLogger } from '../../utils/logger';
import { uiLocale } from '../../i18n/language';
import ConfirmationModal from '../shared/ConfirmationModal';
import {
  AccessGrant,
  GRANT_MAX_DAYS,
  GrantScope,
  fetchAccessGrants,
  shareClass,
  unshareClass,
} from '../../services/classService';
import {
  cardStyle,
  dangerLinkStyle,
  errorMessage,
  errorStyle,
  hintStyle,
  inputStyle,
  labelStyle,
  noticeStyle,
  primaryButtonStyle,
  sectionHeadingStyle,
  statusBadgeStyle,
  tableStyle,
  tdStyle,
  thStyle,
} from './classesStyles';

const logger = createLogger('ClassSharingSection');
const SCOPES: GrantScope[] = ['view', 'grade'];
const DEFAULT_DAYS = 30;

/** A date input's value (local calendar day), `days` from today. */
const dayFromToday = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

interface Props {
  classId: string;
  /** Archived classes keep their grants listed but take no new ones. */
  active: boolean;
}

const ClassSharingSection: React.FC<Props> = ({ classId, active }) => {
  const { t } = useTranslation();
  const [grants, setGrants] = useState<AccessGrant[] | null>(null);
  const [showExpired, setShowExpired] = useState(false);
  const [email, setEmail] = useState('');
  const [scope, setScope] = useState<GrantScope>('view');
  const [until, setUntil] = useState(dayFromToday(DEFAULT_DAYS));
  const [removing, setRemoving] = useState<AccessGrant | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setGrants(await fetchAccessGrants(classId, showExpired));
    } catch (err) {
      logger.error('Could not load access grants', err);
      setError(errorMessage(err, t('studentAdmin.sharing.couldNotLoad')));
    }
  }, [classId, showExpired, t]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (work: () => Promise<unknown>, success: string, fallback: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await work();
      setNotice(success);
      await load();
    } catch (err) {
      logger.error(fallback, err);
      setError(errorMessage(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  const share = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !until) return;
    // The end of the chosen day, in the viewer's time zone.
    const expiresAt = new Date(`${until}T23:59:59`).toISOString();
    run(
      async () => {
        await shareClass(classId, { email: email.trim(), scope, expires_at: expiresAt });
        setEmail('');
      },
      t('studentAdmin.sharing.shared'),
      t('studentAdmin.sharing.couldNotShare')
    );
  };

  const nameOf = (g: AccessGrant) => g.display_name || g.email || g.grantee_id;

  return (
    <section id="tour-class-sharing" style={cardStyle} aria-labelledby="class-sharing-heading">
      <h2 id="class-sharing-heading" style={sectionHeadingStyle}>
        {t('studentAdmin.sharing.title')}
      </h2>
      <p style={{ ...hintStyle, marginTop: 0 }}>{t('studentAdmin.sharing.hint')}</p>
      {error && <div style={{ ...errorStyle, marginTop: '0.75rem' }} role="alert">{error}</div>}
      {notice && <div style={{ ...noticeStyle, marginTop: '0.75rem' }} role="status">{notice}</div>}

      {active && (
        <form onSubmit={share} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', margin: '1rem 0' }}>
          <div style={{ flex: '1 1 220px' }}>
            <label htmlFor="share-email" style={labelStyle}>
              {t('studentAdmin.sharing.email')}
            </label>
            <input
              id="share-email"
              type="email"
              style={{ ...inputStyle, width: '100%' }}
              value={email}
              placeholder={t('classes.emailPlaceholder')}
              onChange={e => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="share-scope" style={labelStyle}>
              {t('studentAdmin.sharing.access')}
            </label>
            <select id="share-scope" style={inputStyle} value={scope} onChange={e => setScope(e.target.value as GrantScope)}>
              {SCOPES.map(s => (
                <option key={s} value={s}>
                  {t(`studentAdmin.sharing.scopes.${s}`)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="share-until" style={labelStyle}>
              {t('studentAdmin.sharing.until')}
            </label>
            <input
              id="share-until"
              type="date"
              style={inputStyle}
              min={dayFromToday(0)}
              max={dayFromToday(GRANT_MAX_DAYS - 1)}
              value={until}
              onChange={e => setUntil(e.target.value)}
            />
          </div>
          <button type="submit" style={primaryButtonStyle} disabled={busy || !email.trim() || !until}>
            {t('studentAdmin.sharing.share')}
          </button>
        </form>
      )}

      {grants && grants.length === 0 ? (
        <p style={{ ...hintStyle, margin: 0 }}>{t('studentAdmin.sharing.none')}</p>
      ) : (
        grants && (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>{t('common.name')}</th>
                  <th style={thStyle}>{t('studentAdmin.sharing.access')}</th>
                  <th style={thStyle}>{t('studentAdmin.sharing.until')}</th>
                  <th style={thStyle}>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {grants.map(g => (
                  <tr key={g.grantee_id}>
                    <td style={tdStyle}>
                      {nameOf(g)}
                      {g.email && g.display_name && <span style={{ ...hintStyle, display: 'block', margin: 0 }}>{g.email}</span>}
                    </td>
                    <td style={tdStyle}>{t(`studentAdmin.sharing.scopes.${g.scope}`)}</td>
                    <td style={tdStyle}>
                      {g.expires_at ? new Date(g.expires_at).toLocaleDateString(uiLocale()) : '—'}{' '}
                      {!g.active && <span style={statusBadgeStyle(false)}>{t('studentAdmin.sharing.expired')}</span>}
                    </td>
                    <td style={tdStyle}>
                      <button
                        type="button"
                        style={dangerLinkStyle}
                        disabled={busy}
                        aria-label={t('studentAdmin.sharing.stopSharingWith', { name: nameOf(g) })}
                        onClick={() => setRemoving(g)}
                      >
                        {t('studentAdmin.sharing.stopSharing')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', marginTop: '0.75rem' }}>
        <input type="checkbox" checked={showExpired} onChange={e => setShowExpired(e.target.checked)} />
        {t('studentAdmin.sharing.showExpired')}
      </label>

      <ConfirmationModal
        isOpen={removing !== null}
        title={t('studentAdmin.sharing.stopTitle')}
        message={removing ? t('studentAdmin.sharing.stopMessage', { name: nameOf(removing) }) : ''}
        confirmText={t('studentAdmin.sharing.stopSharing')}
        loading={busy}
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          const grant = removing;
          setRemoving(null);
          if (grant) {
            run(
              () => unshareClass(classId, grant.grantee_id),
              t('studentAdmin.sharing.unshared'),
              t('studentAdmin.sharing.couldNotUnshare')
            );
          }
        }}
      />
    </section>
  );
};

export default ClassSharingSection;
